import {Component, Prop, Vue} from 'vue-property-decorator';
import debounce from 'lodash.debounce';
import axios from 'axios';
import { HTTP } from '@/services/HttpServices';
import {HighlightableDisplayOption} from '@/components/interfaces/ComponentOptions';
import OverlaySpinner from '@/components/OverlaySpinner.vue';

function defaultConversion(items: any): HighlightableDisplayOption[] {
    const toReturn: HighlightableDisplayOption[] = [];
    for (let i = 0; i < items.length; i++) {
        const id = items[i].id || i;
        toReturn.push({ id: id, name: items[i].name, isHighlighted: false });
    }
    return toReturn;
}

export interface AdditionalParameter {
    field: string;
    value: string;
}

@Component({
    components: {
        OverlaySpinner
    },
    // Let users pass `id` as an attribute but don't put it on the div by default, we manually set it on the input
    inheritAttrs: false,
    model: {
        prop: 'value',
        event: 'input'
    }
})
export default class PredictiveInput extends Vue {
    @Prop({ type: String, required: true })
    private url!: string;
    @Prop({ type: Function, required: false, default: defaultConversion })
    private convert!: (items: any[]) => HighlightableDisplayOption[];
    @Prop({ type: String, required: false, default: '' })
    private value!: string;
    @Prop({ type: Boolean, required: false, default: true })
    private alignLeft!: boolean;
    @Prop({ type: Array, required: false, default: () => [] })
    private additionalParameters!: AdditionalParameter[];
    @Prop({ type: String, required: false, default: ''})
    private placeholder!: string;

    private showDropdown = false;
    private loading = false;
    private items: HighlightableDisplayOption[] = [];
    private cancelSource = axios.CancelToken.source();
    private noResults = false;

    get debounceSearch(): () => void {
        return debounce(this.search, 300, { maxWait: 1000 });
    }

    private async search() {
        try {
            this.loading = true;
            this.cancelSource.cancel();
            this.cancelSource = axios.CancelToken.source();
            let url = this.url + `?searchText=${this.value}`;
            if (this.additionalParameters.length > 0) {
                url += '&' + this.additionalParameters.map(param => param.field + '=' + param.value).join('&');
            }
            const items = await HTTP.get(url, this.cancelSource.token);
            this.items = this.convert(items);
            this.noResults = this.items.length === 0;
        } catch (err) {
            if (axios.isCancel(err)) {
                // Do nothing
            } else {
                this.$notifyError(err);
            }
        } finally {
            this.loading = false;
        }
    }

    toggleDropdown() {
        if (this.showDropdown) {
            this.closeDropdown();
        } else {
            this.showDropdown = true;
        }
    }

    inputChanged(event: any) {
        const newValue = event.target.value && event.target.value;
        if (newValue) {
            this.showDropdown = true;
            this.debounceSearch();
        }
        this.$emit('input', newValue);
    }

    itemClicked(item: HighlightableDisplayOption) {
        this.$emit('input', item.name);
        this.$emit('change', item.id);
        this.$emit('item-changed', item);
        this.closeDropdown();
    }

    closeDropdown() {
        this.showDropdown = false;
        this.noResults = false;
        this.loading = false;
        this.items = [];
        this.$emit('blur');
    }

    highlightNextItem() {
        let tempItems = this.items.slice();
        let highlightIndex = -1;
        for (let i=0; i<tempItems.length; i++ ){
            if (tempItems[i].isHighlighted){
                highlightIndex = i;
                if ( (i+1) < tempItems.length) {
                    tempItems[i].isHighlighted = false;
                }
            }
        }
        if (tempItems.length > (highlightIndex + 1) ){
            tempItems[highlightIndex + 1].isHighlighted = true;
        }
        this.items = tempItems;
    }

    highlightPreviousItem() {
        let tempItems = this.items.slice();
        let highlightIndex = -1;
        for (let i=0; i<tempItems.length; i++ ){
            if (tempItems[i].isHighlighted){
                highlightIndex = i;
                tempItems[i].isHighlighted = false;
            }
        }
        if ( (highlightIndex - 1) >= 0 ){
            tempItems[highlightIndex - 1].isHighlighted = true;
        }
        this.items = tempItems;
    }

    selectHighlightedItem() {
        this.items.forEach( (item) => {
            if (item.isHighlighted) {
                this.itemClicked(item);
            }
        })
    }

    clearFilterInput() {
        const input = this.$refs.search as HTMLInputElement;
        input.value = '';
        input.focus();
        this.closeDropdown();
    }
}